import * as React from 'react';
import { Button, InputDeprecated, Modal } from '@/components';
import { useForm } from 'react-hook-form';
import { formatDateForInput } from '@/shared/helpers/formatDateForInput';
import { CalendarIcon } from '@/components/Icons';
import { useAtom } from 'jotai';
import { editDueDateMutationAtom } from '../model/editDueDateMutationAtom';
import { R } from '../resources/NewbornResources.resx';

export type IFormEditDueDate = {
    dueDate: string;
};

export type EditDueDateModalProps = {
    dueDate: Date;
    onClose: () => void;
};

export const EditDueDateModal = ({
    dueDate,
    onClose,
}: EditDueDateModalProps) => {
    const [{ mutate, isPending, isError }] = useAtom(editDueDateMutationAtom);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IFormEditDueDate>({
        mode: 'onSubmit',
        defaultValues: { dueDate: formatDateForInput(dueDate) },
    });

    const onSubmit = async ({ dueDate }: IFormEditDueDate) => {
        mutate(new Date(dueDate), {
            onSuccess: () => {
                onClose();
            },
        });
    };

    const [minDate, maxDate] = React.useMemo(() => {
        const min = new Date(dueDate);
        const max = new Date(dueDate);
        min.setDate(min.getDate() - 60);
        max.setDate(max.getDate() + 30);
        return [min, max];
    }, [dueDate]);

    return (
        <Modal onClose={onClose} title={R.EditDueDate}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <InputDeprecated
                    type="date"
                    label={R.SelectDate}
                    icon={<CalendarIcon />}
                    inputClassName={
                        errors.dueDate?.message || isError
                            ? 'border border-secondary-red'
                            : undefined
                    }
                    {...register('dueDate', {
                        required: R.EmptyDueDateError,
                        validate: (value) => {
                            const selectedDate = new Date(value);
                            return (
                                (selectedDate >= minDate &&
                                    selectedDate <= maxDate) ||
                                R.OutOfRangeDueDateError
                            );
                        },
                    })}
                    error={
                        errors.dueDate?.message ||
                        (isError ? R.UpdateDueDateServerSideError : undefined)
                    }
                />
                <div className="mt-6 flex justify-end">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isPending}
                    >
                        {R.UpdateDueDateButtonText}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
